import { RefreshTokenInvalidError } from './errors'

const BASE_URL = '/api'

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---- Token helpers (read localStorage directly — client.ts is not a React component) ----

function getStoredUser(): { token: string; refreshToken: string } | null {
  try {
    const stored = localStorage.getItem('cg-auth')
    if (!stored) return null
    const parsed = JSON.parse(stored) as {
      state?: { user?: { token?: string; refreshToken?: string } }
    }
    const user = parsed.state?.user
    if (!user?.token || !user.refreshToken) return null
    return { token: user.token, refreshToken: user.refreshToken }
  } catch {
    return null
  }
}

function getToken(): string | null {
  return getStoredUser()?.token ?? null
}

function getRefreshToken(): string | null {
  return getStoredUser()?.refreshToken ?? null
}

function isTokenExpiredOrExpiringSoon(token: string): boolean {
  try {
    const payloadB64 = token.split('.')[1]
    if (!payloadB64) return true
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(padded)) as { exp?: number }
    if (payload.exp === undefined) return true
    return Date.now() / 1000 >= payload.exp - 30
  } catch {
    return true
  }
}

// ---- Refresh with concurrency guard ----
// Collapses parallel callers (e.g. multiple queries firing on page load) onto one in-flight request.

let refreshPromise: Promise<string> | null = null

async function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    // Dynamic imports break the circular dep: auth.ts imports api from client.ts
    const { refreshAccessToken } = await import('./auth')
    const refreshToken = getRefreshToken()
    if (!refreshToken) throw new Error('No refresh token')
    const newToken = await refreshAccessToken(refreshToken)
    const { useAuthStore } = await import('@/store/auth')
    useAuthStore.getState().setToken(newToken)
    return newToken
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

async function doLogout(): Promise<void> {
  const { useAuthStore } = await import('@/store/auth')
  useAuthStore.getState().logout()
  window.location.href = '/login'
}

// ---- Core request function ----

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let token = getToken()

  if (token && isTokenExpiredOrExpiringSoon(token)) {
    try {
      token = await doRefresh()
    } catch (err) {
      if (err instanceof RefreshTokenInvalidError) await doLogout()
      throw new ApiError(401, 'Session expired')
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers })

  if (response.status === 401) {
    try {
      const newToken = await doRefresh()
      const retryHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(init.headers as Record<string, string>),
        Authorization: `Bearer ${newToken}`,
      }
      const retryResponse = await fetch(`${BASE_URL}${path}`, { ...init, headers: retryHeaders })
      if (retryResponse.status === 401) {
        await doLogout()
        throw new ApiError(401, 'Session expired')
      }
      if (!retryResponse.ok) {
        const body = await retryResponse.text()
        let message = body
        try {
          message = (JSON.parse(body) as { detail?: string }).detail ?? body
        } catch { /* use raw body */ }
        throw new ApiError(retryResponse.status, message)
      }
      if (retryResponse.status === 202 || retryResponse.status === 204) return undefined as T
      return retryResponse.json() as Promise<T>
    } catch (err) {
      if (err instanceof ApiError) throw err
      if (err instanceof RefreshTokenInvalidError) await doLogout()
      throw new ApiError(401, 'Session expired')
    }
  }

  if (!response.ok) {
    const body = await response.text()
    let message = body
    try {
      message = (JSON.parse(body) as { detail?: string }).detail ?? body
    } catch { /* use raw body */ }
    throw new ApiError(response.status, message)
  }

  if (response.status === 202 || response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export { ApiError }
